import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge, Loader2, Pencil, Plus, Route, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StageTag, StatusPill } from "@/components/StatusPill";
import { AiNote } from "@/components/AiNote";
import { JourneyTimeline, RecordersBlock } from "@/components/JourneyTimeline";
import { JourneyView } from "@/components/JourneyView";
import { ProjectFormDialog } from "@/components/ProjectFormDialog";
import { ProjectReportButton } from "@/components/ProjectReportButton";
import { UnverifiedBadge } from "@/components/UnverifiedBadge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DayField } from "@/components/DayField";
import { toastWithUndo } from "@/lib/undo";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, fmtCr, fmtDay, fmtPct, fmtWhen, PROGRESS, TERM_COLORS } from "@/lib/api";

const Row = ({ label, value }) => (
  <div className="bg-card p-3">
    <div className="label-caps">{label}</div>
    <div className="mt-1 text-xs font-medium">{value || "—"}</div>
  </div>
);

export const ProjectDetailSheet = ({ projectId, open, onOpenChange, meta, onChanged }) => {
  const { superAdmin, sees, shows, writable } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [journey, setJourney] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [entry, setEntry] = useState({
    date: new Date().toISOString().slice(0, 10),
    phase: "Hot Trials",
    kpi: "",
    uom: "",
    target_value: "",
    actual_value: "",
    remark: "",
  });

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    api
      .get(`/projects/${projectId}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const p = data?.project;
  const why = data?.why;
  const colour = why ? PROGRESS[why.key].color : undefined;

  const addEntry = async (e) => {
    e.preventDefault();
    try {
      const body = {
        date: entry.date,
        phase: entry.phase,
        kpi: entry.kpi || null,
        uom: entry.uom || null,
        target_value: entry.target_value === "" ? null : Number(entry.target_value),
        actual_value: entry.actual_value === "" ? null : Number(entry.actual_value),
        met:
          entry.target_value !== "" && entry.actual_value !== ""
            ? Number(entry.actual_value) >= Number(entry.target_value)
            : null,
        remark: entry.remark || null,
      };
      const { data } = await api.post(`/projects/${projectId}/compliance`, body);
      toastWithUndo(
        data.awaiting_verification
          ? "Reading sent for approval — the deep-dive is unchanged until then."
          : "Reading saved.",
        async () => {
          if (data.awaiting_verification && data.pending?.id) {
            await api.post(`/pending/${data.pending.id}/withdraw`);
          } else {
            await api.delete(`/projects/${projectId}/compliance`, {
              params: { date: body.date, phase: body.phase, kpi: body.kpi || "" },
            });
          }
          load();
          onChanged?.();
        },
        { done: "Reading taken back." },
      );
      setEntry({ ...entry, target_value: "", actual_value: "", remark: "" });
      load();
      onChanged?.();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeEntry = async (row) => {
    try {
      const { data } = await api.delete(`/projects/${projectId}/compliance`, {
        params: { date: row.date, phase: row.phase, kpi: row.kpi || "" },
      });
      toastWithUndo(
        data?.awaiting_verification
          ? "Sent for approval — the reading stays until then."
          : "Reading removed.",
        async () => {
          if (data?.awaiting_verification && data.pending?.id) {
            await api.post(`/pending/${data.pending.id}/withdraw`);
          } else {
            await api.post(`/projects/${projectId}/compliance`, {
              date: row.date,
              phase: row.phase,
              kpi: row.kpi || null,
              uom: row.uom || null,
              target_value: row.target_value ?? null,
              actual_value: row.actual_value ?? null,
              met: row.met ?? null,
              remark: row.remark || null,
            });
          }
          load();
        },
        { done: "Reading put back." },
      );
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          data-testid="project-detail-sheet"
          className="w-full overflow-y-auto border-l bg-background sm:max-w-2xl"
          onPointerDownOutside={(e) => {
            // Pressing Undo on a toast is not "clicking away" — the officer stays where they were.
            if (e.target?.closest?.("[data-sonner-toast]")) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (e.target?.closest?.("[data-sonner-toast]")) e.preventDefault();
          }}
        >
          <SheetHeader className="text-left">
            <div className="label-caps">{p?.code || "Project"}</div>
            <SheetTitle className="font-display text-2xl font-bold leading-tight tracking-tight">
              {loading ? "Loading…" : p?.title}
            </SheetTitle>
            <SheetDescription>
              {p ? `${p.plant} · ${p.dept_shop || "—"} · ${p.project_type || "—"}` : ""}
            </SheetDescription>
            {p && !data.can_edit && (
              <div
                className="mt-2 border border-border bg-card px-3 py-2 font-data text-[11px] uppercase tracking-[0.14em] text-muted-foreground"
                data-testid="detail-view-only"
              >
                View only · the {p.plant} SPOC owns this register
              </div>
            )}
            {p?.unverified && (
              <div className="mt-2 border border-border bg-card p-3" data-testid="detail-unverified">
                <UnverifiedBadge mark={p.unverified} testId="detail-unverified-badge" />
                <p className="mt-2 text-xs text-muted-foreground">
                  {p.unverified.by} has filled {p.unverified.count} change(s) that{" "}
                  {p.unverified.waiting_on || `the ${p.plant} SPOC`} has yet to verify. What you see
                  below, and what goes into the Ministry and SteerCo packs, is still the verified
                  data.
                </p>
                <ul className="mt-2 space-y-1">
                  {(p.unverified.fields || []).map((c, i) => (
                    <li key={i} className="font-data text-[11px]">
                      <span className="text-muted-foreground">{c.label}: </span>
                      <span className="line-through opacity-60">{String(c.from ?? "blank")}</span>
                      <span className="mx-1.5">→</span>
                      <span className="font-bold">{String(c.to ?? "blank")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {p?.data_gaps?.length > 0 && (
              <div
                className="mt-2 border p-3"
                style={{ borderColor: "hsl(var(--watch))" }}
                data-testid="detail-needs-fix"
              >
                <div className="label-caps" style={{ color: "hsl(var(--watch))" }}>
                  Still to be filled{p.data_gaps_from ? ` — came in with ${p.data_gaps_from}` : ""}
                </div>
                <ul className="mt-2 space-y-1">
                  {p.data_gaps.map((g, i) => (
                    <li key={i} className="text-[11px]" data-testid="detail-gap">
                      <b>{g.label}</b>
                      <span className="text-muted-foreground"> — {g.note}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  The workbook was still brought in. Press Update and fill these here — each one
                  clears itself as it is filled.
                </p>
              </div>
            )}
          </SheetHeader>

          {loading && (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {p && why && (
            <div className="mt-5 space-y-7">
              <div className="border border-border bg-card p-5" style={{ borderLeftWidth: 3, borderLeftColor: colour }}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-xl font-bold" style={{ color: colour }} data-testid="detail-progress-label">
                      {why.label}
                    </span>
                    <StageTag stage={p.status} />
                    {p.term && (
                      <span
                        className="border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                        style={{ borderColor: TERM_COLORS[p.term], color: TERM_COLORS[p.term] }}
                        data-testid="detail-term"
                      >
                        {p.term}
                      </span>
                    )}
                    {p.provider_type === "Startup" && (
                      <span className="border border-primary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                        Startup
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {shows("projects.journey") && (
                      <Button
                        variant="outline"
                        className="h-9 rounded-none text-xs font-bold uppercase tracking-widest"
                        data-testid="open-journey-view"
                        onClick={() => setJourney(true)}
                      >
                        <Route className="mr-2 h-3.5 w-3.5" /> Journey view
                      </Button>
                    )}
                    {sees("compliance_data") && (
                      <Button
                        variant="outline"
                        className="h-9 rounded-none text-xs font-bold uppercase tracking-widest"
                        data-testid="open-compliance-data"
                        title="Day-wise readings, KPI against actual and cumulative compliance"
                        onClick={() => {
                          onOpenChange?.(false);
                          navigate(`/compliance-data?q=${encodeURIComponent(p.code || "")}&plant=${p.plant || ""}`);
                        }}
                      >
                        <Gauge className="mr-2 h-3.5 w-3.5" /> Compliance data
                      </Button>
                    )}
                    {shows("projects.export") && (
                      <ProjectReportButton project={p} testId="detail-project-report" />
                    )}
                    {data.can_edit && !writable("projects") && (
                      <span
                        className="font-data border border-[hsl(var(--watch))] px-2 py-1 text-[10px] uppercase tracking-wider text-[hsl(var(--watch))]"
                        data-testid="detail-view-only"
                      >
                        reading only
                      </span>
                    )}
                    {data.can_edit && writable("projects") && (
                      <Button
                        className="h-9 rounded-none text-xs font-bold uppercase tracking-widest"
                        data-testid="open-edit-project"
                        onClick={() => setEditing(true)}
                      >
                        <Pencil className="mr-2 h-3.5 w-3.5" /> Update
                      </Button>
                    )}
                    {superAdmin && (
                      <Button
                        variant="outline"
                        className="h-9 rounded-none border-[hsl(var(--at-risk))] text-xs font-bold uppercase tracking-widest text-[hsl(var(--at-risk))]"
                        data-testid="delete-project"
                        onClick={() => setRemoving(true)}
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="label-caps mb-1">Why</div>
                  <ul className="space-y-1.5 text-sm">
                    {why.reasons.map((r) => (
                      <li key={r} className="flex gap-2" data-testid="detail-reason">
                        <span style={{ color: colour }}>•</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {!!(why.to_on_track || []).length && (
                  <div className="mt-4 border-t border-border pt-3" data-testid="detail-to-on-track">
                    <div className="label-caps mb-1" style={{ color: "hsl(var(--healthy))" }}>
                      To read On Track
                    </div>
                    <ul className="space-y-1.5 text-sm">
                      {why.to_on_track.map((a) => (
                        <li key={a} className="flex gap-2">
                          <span style={{ color: "hsl(var(--healthy))" }}>•</span>
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {!!(why.how || []).length && (
                  <div className="mt-4 border-t border-border pt-3" data-testid="detail-how">
                    <div className="label-caps mb-1">How this is worked out</div>
                    <ul className="space-y-1 text-[11px] text-muted-foreground">
                      {why.how.map((h) => (
                        <li key={h}>· {h}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-4 border-t border-border pt-3 text-sm">
                  <span className="label-caps">What next</span>
                  <p className="mt-1">{why.next_step}</p>
                </div>
                <p
                  className="font-data mt-3 text-[11px] text-muted-foreground"
                  data-testid="detail-stamp-line"
                >
                  Last updated on {fmtDay(p.last_updated_on)} by{" "}
                  {p.last_updated_by || p.entered_by || "—"}
                  {p.entered_by && p.entered_by !== p.last_updated_by
                    ? ` · entered by ${p.entered_by}`
                    : ""}
                  {p.approved_by && p.approved_by !== (p.last_updated_by || p.entered_by)
                    ? ` · approved by ${p.approved_by}`
                    : ""}
                  {p.source_file ? ` · from ${p.source_file}` : ""}
                </p>
              </div>

              <div className="border-t border-border pt-6">
                <JourneyTimeline
                  project={p}
                  meta={meta}
                  canEdit={false}
                  onChanged={() => {
                    load();
                    onChanged?.();
                  }}
                />
              </div>

              {shows("projects.ai") && (
                <AiNote
                  kind="project"
                  params={{ project_id: projectId }}
                  cta="Summarise this use case"
                  blurb="Where this stands, what is holding it up and what should happen next — written from this register entry, its stage history and its readings."
                  testId="ai-note-project"
                />
              )}

              <div>
                <div className="label-caps mb-2">Details</div>
                <div className="grid grid-cols-2 gap-px bg-border">
                  <Row label="Focus area" value={p.focus_area} />
                  <Row label="Term" value={p.term || "To be updated"} />
                  <Row label="Expected date of completion" value={p.target_completion_on || "To be updated"} />
                  <Row label="Target date / timeline" value={p.timeline} />
                  <Row label="Solution provider" value={p.vendor || "In-house"} />
                  <Row label="Provider type" value={p.provider_type} />
                  <Row label="Who is looking after it" value={p.owner_name} />
                  <Row label="Cost" value={p.project_cost_lakh ? `₹${p.project_cost_lakh} Lakh` : null} />
                  <Row label="Expected benefit" value={fmtCr(p.est_value_cr)} />
                  <Row label="Approval route" value={p.approval_route} />
                  <Row label="Unit" value={p.plant} />
                </div>
                {p.scope && (
                  <div className="mt-4">
                    <div className="label-caps mb-1">What the project does</div>
                    <p className="text-sm leading-relaxed">{p.scope}</p>
                  </div>
                )}
                {p.remarks && (
                  <div className="mt-4">
                    <div className="label-caps mb-1">Where it stands today</div>
                    <p className="border-l-2 border-primary bg-muted px-3 py-2 text-sm leading-relaxed">
                      {p.remarks}
                    </p>
                  </div>
                )}
              </div>

              {(p.is_target_value || data.compliance.length > 0 ||
                (meta?.deep_dive_stages || []).includes(p.phase)) && (
                <div>
                  <div className="label-caps mb-2">
                    Day-wise readings (trials &amp; go-live)
                  </div>

                  {data.compliance_summary?.readings > 0 && (
                    <div className="mb-3 grid gap-px bg-border sm:grid-cols-4" data-testid="compliance-summary">
                      {[
                        ["Yesterday", fmtPct(data.compliance_summary.yesterday)],
                        ["Till yesterday", fmtPct(data.compliance_summary.till_yesterday)],
                        ["Last 7 days", fmtPct(data.compliance_summary.moving_avg_7)],
                        ["Days target met", `${data.compliance_summary.days_met}/${data.compliance_summary.readings}`],
                      ].map(([label, value]) => (
                        <div key={label} className="bg-card p-2">
                          <div className="font-data text-[10px] uppercase tracking-widest text-muted-foreground">
                            {label}
                          </div>
                          <div className="font-data text-base font-bold">{value}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {data.compliance.length ? (
                    <div className="overflow-x-auto border border-border">
                      <table className="w-full text-xs" data-testid="compliance-table">
                        <thead className="bg-muted">
                          <tr className="text-left">
                            {["Date", "Stage", "KPI", "Target", "Actual", "Met?", "Remark", ""].map((h) => (
                              <th key={h} className="label-caps px-3 py-2">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {data.compliance.map((row) => (
                            <tr key={`${row.date}-${row.phase}-${row.kpi || ""}`} data-testid={`compliance-row-${row.date}`}>
                              <td className="font-data px-3 py-2">{row.date}</td>
                              <td className="px-3 py-2">{row.phase}</td>
                              <td className="px-3 py-2">
                                {row.kpi || "—"}
                                {row.uom ? ` (${row.uom})` : ""}
                              </td>
                              <td className="font-data px-3 py-2">{row.target_value ?? "—"}</td>
                              <td className="font-data px-3 py-2">{row.actual_value ?? "—"}</td>
                              <td className="px-3 py-2">
                                <span
                                  style={{
                                    color: row.met
                                      ? PROGRESS.on_track.color
                                      : PROGRESS.delayed.color,
                                  }}
                                >
                                  {row.met === null || row.met === undefined ? "—" : row.met ? "Yes" : "No"}
                                </span>
                              </td>
                              <td className="px-3 py-2">{row.remark || "—"}</td>
                              <td className="px-3 py-2 text-right">
                                {data.can_record && (
                                  <button
                                    type="button"
                                    data-testid={`delete-compliance-${row.date}`}
                                    onClick={() => removeEntry(row)}
                                    className="text-muted-foreground transition-colors duration-200 hover:text-[hsl(var(--at-risk))]"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No daily readings recorded yet.
                    </p>
                  )}

                  {data.can_record && (
                    <form
                      onSubmit={addEntry}
                      className="mt-3 grid gap-2 border border-border bg-card p-3 sm:grid-cols-4"
                      data-testid="compliance-form"
                    >
                      <DayField
                        testId="compliance-date"
                        format="iso"
                        value={entry.date}
                        onChange={(v) => setEntry({ ...entry, date: v })}
                      />
                      <Select
                        value={entry.phase}
                        onValueChange={(v) => setEntry({ ...entry, phase: v })}
                      >
                        <SelectTrigger className="h-10 rounded-none text-xs" data-testid="compliance-phase">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none bg-popover">
                          {(meta?.compliance_phases || ["Hot Trials", "Go-Live"]).map((ph) => (
                            <SelectItem key={ph} value={ph}>
                              {ph}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="KPI (e.g. Yield %)"
                        className="h-10 rounded-none text-xs"
                        data-testid="compliance-kpi"
                        value={entry.kpi}
                        onChange={(e) => setEntry({ ...entry, kpi: e.target.value })}
                      />
                      <Input
                        placeholder="Unit (t/day, %)"
                        className="h-10 rounded-none text-xs"
                        data-testid="compliance-uom"
                        value={entry.uom}
                        onChange={(e) => setEntry({ ...entry, uom: e.target.value })}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Target"
                        className="h-10 rounded-none text-xs"
                        data-testid="compliance-target"
                        value={entry.target_value}
                        onChange={(e) => setEntry({ ...entry, target_value: e.target.value })}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Actual"
                        className="h-10 rounded-none text-xs"
                        data-testid="compliance-actual"
                        value={entry.actual_value}
                        onChange={(e) => setEntry({ ...entry, actual_value: e.target.value })}
                      />
                      <Input
                        placeholder="Remark"
                        className="h-10 rounded-none text-xs"
                        data-testid="compliance-remark"
                        value={entry.remark}
                        onChange={(e) => setEntry({ ...entry, remark: e.target.value })}
                      />
                      <Button
                        type="submit"
                        className="h-10 rounded-none text-xs font-bold uppercase"
                        data-testid="add-compliance-button"
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" /> Add reading
                      </Button>
                    </form>
                  )}

                  <div className="mt-4 border-t border-border pt-4">
                    <RecordersBlock
                      project={p}
                      canEdit={data.can_edit}
                      onChanged={() => {
                        load();
                        onChanged?.();
                      }}
                    />
                  </div>
                </div>
              )}

              <div>
                <div className="label-caps mb-2">What changed (audit trail)</div>
                {data.activity.length ? (
                  <ol className="space-y-2" data-testid="activity-trail">
                    {data.activity.map((a, i) => (
                      <li key={i} className="border-l-2 border-border pl-3 text-xs">
                        <div className="font-medium">{a.summary}</div>
                        <div className="font-data text-[11px] text-muted-foreground">
                          {fmtWhen(a.at)} · {a.actor_name} ({a.actor_email})
                        </div>
                        {a.changes?.length > 0 && (
                          <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                            {a.changes.map((c, ci) => (
                              <li key={`${c.field}-${ci}`}>
                                {c.label}: <s>{String(c.from ?? "—")}</s> → <b>{String(c.to ?? "—")}</b>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No changes recorded in the portal yet. This project came from{" "}
                    {p.source_file || "the unit register"}.
                  </p>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <ProjectFormDialog
        open={editing}
        onOpenChange={setEditing}
        project={p}
        meta={meta}
        onSaved={() => {
          load();
          onChanged?.();
        }}
      />

      <JourneyView projectId={projectId} open={journey} onOpenChange={setJourney} />

      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title="Remove this use case from the register?"
        description={
          p
            ? `${p.code} — ${p.title} (${p.plant}). Its readings, its journey and anything waiting on it go with it. Nothing is recorded about this removal.`
            : ""
        }
        confirmLabel="Yes, remove it"
        cancelLabel="No, keep it"
        tone="danger"
        testId="confirm-delete-project"
        onConfirm={async () => {
          setRemoving(false);
          try {
            await api.delete(`/projects/${projectId}`);
            toast.success(`${p.code} is off the register.`);
            onOpenChange(false);
            onChanged?.();
          } catch (e) {
            toast.error(apiError(e));
          }
        }}
      />
    </>
  );
};
